// Iter-185.BM (AUDIT #10) — PostHog retargeting audience seeds.
//
// Per `/app/memory/AUDIT_2026-02.md` TIER 2 #10 (RevOps Director ·
// GA4 Analytics Specialist):
//
//   "Build 3 retargeting audiences in PostHog → push to Meta Custom
//    Audiences + Google Ads Audience Manager. ROI estimate: ~$1.20 CPA
//    on retargeting vs. ~$8 CPA on cold."
//
// PostHog audiences are defined in the PostHog UI as "users who fired
// event X in the last N days AND did NOT fire event Y". This module
// gives us the X anchors — small, deterministic, predictable events
// that we can wire at the right tripwire locations:
//
//   1. `audience_funnel_abandoned`        — Intake form started, ≥2 steps
//                                            advanced, no submit within
//                                            session.
//   2. `audience_calculator_engaged_no_quote`
//                                          — Calculator slider moved, no
//                                            CTA click within session.
//   3. `audience_article_binge_no_click`   — 3+ article_view events in a
//                                            single session, no
//                                            affiliate_click.
//
// Each tripwire is intentionally fired ONCE per session via
// sessionStorage so PostHog cohort math stays clean (no inflation from
// repeat-fires within the same visit). The corresponding conversion
// event the operator excludes against:
//
//   1 → `modal_submit` OR `zip_form_success`
//   2 → `calculator_result_cta_click`
//   3 → `affiliate_click`

import { track, events } from "./analytics";

const STORAGE_PREFIX = "carsavr_audience_";
const STORAGE_TTL_HOURS = 6;
const TTL_MS = STORAGE_TTL_HOURS * 60 * 60 * 1000;

function alreadyFiredThisSession(key) {
  if (typeof window === "undefined") return true;
  try {
    const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    // Re-fire after TTL so a long-running tab still re-qualifies the
    // session if the visitor lingers all afternoon.
    if (Date.now() - ts > TTL_MS) {
      sessionStorage.removeItem(`${STORAGE_PREFIX}${key}`);
      return false;
    }
    return true;
  } catch {
    return true; // private mode → never fire (safer than spam)
  }
}

function markFired(key) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, String(Date.now()));
  } catch {
    /* ignore */
  }
}

/** Fire the "funnel abandoned" audience tripwire.
 *  Call from intake forms once the user advances past step 1 (so we know
 *  they engaged, not just opened the modal). */
export function fireAudienceFunnelAbandoned(meta = {}) {
  const key = "funnel_abandoned";
  if (alreadyFiredThisSession(key)) return;
  markFired(key);
  track(events.AUDIENCE_FUNNEL_ABANDONED, {
    category: meta.category || "unknown",
    step_reached: meta.step_reached || null,
    surface: meta.surface || "intake_form",
  });
}

/** Fire the "calculator engaged, no quote" audience tripwire.
 *  Call from calculator slider onChange after the first input mutation. */
export function fireAudienceCalculatorEngaged(meta = {}) {
  const key = "calculator_engaged";
  if (alreadyFiredThisSession(key)) return;
  markFired(key);
  track(events.AUDIENCE_CALCULATOR_ENGAGED_NO_QUOTE, {
    calculator_type: meta.calculator_type || "savings",
    estimated_annual: meta.estimated_annual || null,
    page_location: meta.page_location || null,
  });
}

/** Increment the per-session article view counter; fire the audience
 *  tripwire when the counter crosses 3. Call from ArticlePage on mount. */
export function bumpArticleViewAndMaybeFire(meta = {}) {
  if (typeof window === "undefined") return;
  const COUNTER_KEY = `${STORAGE_PREFIX}article_views`;
  try {
    const current = Number(sessionStorage.getItem(COUNTER_KEY) || "0");
    const next = current + 1;
    sessionStorage.setItem(COUNTER_KEY, String(next));
    if (next === 3) {
      // Exact-3 trigger so we don't spam PostHog on every subsequent
      // pageview in the same session.
      track(events.AUDIENCE_ARTICLE_BINGE_NO_CLICK, {
        article_slug: meta.article_slug || null,
        page_location: meta.page_location || null,
      });
    }
  } catch {
    /* ignore */
  }
}
